// repo/primary-care-connect/frontend/src/pages/Portal/OhtAdmin/Insights/InsightsPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  getOhtInsights,
  type OhtInsightsResult,
  type OhtPatientBaselineStatus,
  type OhtReportRow,
} from "../../../../api/ohtAdmin";
import {
  downloadReportCsv as downloadStructuredReportCsv,
  downloadReportPdf,
  type Report,
} from "../../../../utils/reportExport";
import { PageOutline } from "../components/PageOutline";
import "../Overview/OverviewPage.css";
import "./InsightsPage.css";

type TimelineKey = "last30" | "last90" | "ytd" | "all" | "custom";

type TimelineState = {
  key: TimelineKey;
  from: string | null;
  to: string | null;
};

type ReportSummary = {
  requests: number;
  attached: number;
  pipeline: number;
  declaredCapacity: number;
  remainingCapacity: number;
  target: number;
};

type ReportDisplayRow = {
  key: string;
  fsa: string | null;
  city: string | null;
  clinics: number;
  doctors: number;
  attachedCount: number;
  pipelineCount: number;
  pacePerDay: number | null;
  avgDaysToAttachment: number | null;
  remainingCapacity: number;
  targetPatients: number | null;
  estimatedDaysToTarget: number | null;
};

type ReportDisplayAccumulator = ReportDisplayRow & {
  clinicIds: Set<number>;
  weightedTtaTotal: number;
  weightedTtaCount: number;
};

const TODAY_ISO = new Date().toISOString().slice(0, 10);

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function firstDayOfYear(): string {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

function resolveTimeline(key: TimelineKey): TimelineState {
  if (key === "last30") {
    return {
      key,
      from: isoDaysAgo(30),
      to: TODAY_ISO,
    };
  }

  if (key === "last90") {
    return {
      key,
      from: isoDaysAgo(90),
      to: TODAY_ISO,
    };
  }

  if (key === "ytd") {
    return {
      key,
      from: firstDayOfYear(),
      to: TODAY_ISO,
    };
  }

  if (key === "all") {
    return {
      key,
      from: null,
      to: TODAY_ISO,
    };
  }

  return {
    key,
    from: isoDaysAgo(90),
    to: TODAY_ISO,
  };
}

function timelineLabel(timeline: TimelineState): string {
  if (timeline.key === "last30") return "Last 30 days";
  if (timeline.key === "last90") return "Last 90 days";
  if (timeline.key === "ytd") return "Year to date";
  if (timeline.key === "all") return "All time";

  if (timeline.from && timeline.to) {
    return `${timeline.from} to ${timeline.to}`;
  }

  return "Custom timeline";
}

function baselineLabel(value: OhtPatientBaselineStatus): string {
  if (value === "unattached") return "MOH-unattached patients";
  if (value === "attached") return "Patients switching / already attached";
  return "All patients";
}

function fmtNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return Number(value).toLocaleString();
}

function fmtDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  const n = Math.round(Number(value));

  if (n === 0) return "0 days";
  if (n === 1) return "1 day";
  return `${n.toLocaleString()} days`;
}

function fmtPace(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(2)} / day`;
}

function fmtTargetEta(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "No pace";
  }

  if (Number(value) <= 0) return "Reached";
  return fmtDays(value);
}

function targetClass(row: ReportDisplayRow): string {
  const eta = row.estimatedDaysToTarget;

  if (row.targetPatients === null || row.targetPatients === undefined) {
    return "oht-report-pill oht-report-pill--muted";
  }

  if (eta === 0) return "oht-report-pill oht-report-pill--good";
  if (eta === null || eta === undefined) {
    return "oht-report-pill oht-report-pill--danger";
  }

  return "oht-report-pill oht-report-pill--warn";
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(
    new Set(
      values
        .map((v) => String(v || "").trim())
        .filter((v) => v.length > 0)
    )
  ).sort((a, b) => a.localeCompare(b));
}

function getWindowDays(insights: OhtInsightsResult | null): number {
  const days = Number(insights?.reportWindow?.days || 0);

  if (Number.isFinite(days) && days > 0) return days;

  return 30;
}

function buildDisplayRows(
  rows: OhtReportRow[],
  windowDays: number
): ReportDisplayRow[] {
  const map = new Map<string, ReportDisplayAccumulator>();

  for (const row of rows) {
    const fsa = row.fsa || "—";
    const city = row.city || "—";
    const key = `${fsa}__${city}`;

    const existing =
      map.get(key) ||
      ({
        key,
        fsa: row.fsa || null,
        city: row.city || null,
        clinics: 0,
        doctors: 0,
        attachedCount: 0,
        pipelineCount: 0,
        pacePerDay: null,
        avgDaysToAttachment: null,
        remainingCapacity: 0,
        targetPatients: null,
        estimatedDaysToTarget: null,
        clinicIds: new Set<number>(),
        weightedTtaTotal: 0,
        weightedTtaCount: 0,
      } satisfies ReportDisplayAccumulator);

    if (row.clinicId && row.clinicId > 0) {
      existing.clinicIds.add(row.clinicId);
    }

    existing.doctors += Number(row.doctors || 0);
    existing.attachedCount += Number(row.attachedCount || 0);
    existing.pipelineCount += Number(row.pipelineCount || 0);
    existing.remainingCapacity += Number(row.remainingCapacity || 0);

    if (
      row.avgDaysToAttachment !== null &&
      row.avgDaysToAttachment !== undefined &&
      Number(row.attachedCount || 0) > 0
    ) {
      existing.weightedTtaTotal +=
        Number(row.avgDaysToAttachment) * Number(row.attachedCount || 0);
      existing.weightedTtaCount += Number(row.attachedCount || 0);
    }

    if (row.targetPatients !== null && row.targetPatients !== undefined) {
      existing.targetPatients = Math.max(
        Number(existing.targetPatients || 0),
        Number(row.targetPatients || 0)
      );
    }

    map.set(key, existing);
  }

  return Array.from(map.values())
    .map((row) => {
      const pace =
        windowDays > 0 ? Number(row.attachedCount || 0) / windowDays : null;

      const avgTta =
        row.weightedTtaCount > 0
          ? row.weightedTtaTotal / row.weightedTtaCount
          : null;

      let eta: number | null = null;

      if (row.targetPatients !== null && row.targetPatients !== undefined) {
        const remainingToTarget =
          Number(row.targetPatients || 0) - Number(row.attachedCount || 0);

        if (remainingToTarget <= 0) {
          eta = 0;
        } else if (pace && pace > 0) {
          eta = Math.ceil(remainingToTarget / pace);
        }
      }

      return {
        key: row.key,
        fsa: row.fsa,
        city: row.city,
        clinics: row.clinicIds.size,
        doctors: row.doctors,
        attachedCount: row.attachedCount,
        pipelineCount: row.pipelineCount,
        pacePerDay: pace,
        avgDaysToAttachment: avgTta,
        remainingCapacity: row.remainingCapacity,
        targetPatients: row.targetPatients,
        estimatedDaysToTarget: eta,
      };
    })
    .sort((a, b) => {
      const fsaCompare = String(a.fsa || "").localeCompare(String(b.fsa || ""));
      if (fsaCompare !== 0) return fsaCompare;

      return String(a.city || "").localeCompare(String(b.city || ""));
    });
}

function buildSummary(rows: OhtReportRow[]): ReportSummary {
  return rows.reduce(
    (acc, row) => {
      acc.requests += Number(row.requestCount || 0);
      acc.attached += Number(row.attachedCount || 0);
      acc.pipeline += Number(row.pipelineCount || 0);
      acc.declaredCapacity += Number(row.declaredCapacity || 0);
      acc.remainingCapacity += Number(row.remainingCapacity || 0);
      acc.target += Number(row.targetPatients || 0);
      return acc;
    },
    {
      requests: 0,
      attached: 0,
      pipeline: 0,
      declaredCapacity: 0,
      remainingCapacity: 0,
      target: 0,
    }
  );
}

function buildReportExport({
  rows,
  insights,
  timeline,
  cityFilter,
  fsaFilter,
  baselineStatus,
  summary,
}: {
  rows: ReportDisplayRow[];
  insights: OhtInsightsResult | null;
  timeline: TimelineState;
  cityFilter: string;
  fsaFilter: string;
  baselineStatus: OhtPatientBaselineStatus;
  summary: ReportSummary;
}): Report {
  const ohtName = insights?.oht?.name || "OHT";
  const ohtCode = insights?.oht?.code ? ` · ${insights.oht.code}` : "";

  return {
    pageLabel: "OHT Admin Portal — Reports",
    subtitle: `${ohtName}${ohtCode} · ${timelineLabel(timeline)} · ${
      cityFilter || "All cities"
    } · ${fsaFilter || "All FSAs"} · ${baselineLabel(baselineStatus)}`,
    chapters: [
      {
        title: "Attachment Report",
        summary:
          "FSA and city-level attachment, pipeline, pace, capacity, target, and ETA report generated from live platform data.",
        sections: [
          {
            type: "kpi",
            heading: "Summary",
            items: [
              {
                label: "Requests",
                value: fmtNumber(summary.requests),
                hint: "Submitted requests in selected timeline",
                tone: "blue",
              },
              {
                label: "Attached",
                value: fmtNumber(summary.attached),
                hint: "Completed attachments in selected timeline",
                tone: "green",
              },
              {
                label: "Pipeline",
                value: fmtNumber(summary.pipeline),
                hint: "Current active patient flow",
                tone: "amber",
              },
              {
                label: "Remaining Capacity",
                value: fmtNumber(summary.remainingCapacity),
                hint: "Current reported remaining capacity",
                tone: "blue",
              },
              {
                label: "Target",
                value: fmtNumber(summary.target),
                hint: "Active target coverage",
                tone: "grey",
              },
            ],
          },
          {
            type: "table",
            heading: "Report Table",
            description:
              "Pipeline includes submitted, in-review, waitlisted, and accepted-pending-attachment requests. Pace is completed attachments per day.",
            columns: [
              "FSA",
              "City",
              "Clinics",
              "Doctors",
              "Attached",
              "Pipeline",
              "Pace",
              "Avg TTA",
              "Remaining Capacity",
              "Target",
              "ETA",
            ],
            aligns: [
              "left",
              "left",
              "right",
              "right",
              "right",
              "right",
              "right",
              "right",
              "right",
              "right",
              "right",
            ],
            colWeights: [0.65, 1.25, 0.7, 0.7, 0.8, 0.8, 0.9, 0.9, 1.15, 0.9, 0.9],
            rows: rows.length
              ? rows.map((row) => [
                  row.fsa || "—",
                  row.city || "—",
                  row.clinics,
                  row.doctors,
                  row.attachedCount,
                  row.pipelineCount,
                  fmtPace(row.pacePerDay),
                  fmtDays(row.avgDaysToAttachment),
                  row.remainingCapacity,
                  row.targetPatients ?? "—",
                  fmtTargetEta(row.estimatedDaysToTarget),
                ])
              : [["—", "No matching report rows", "", "", "", "", "", "", "", "", ""]],
          },
        ],
      },
    ],
  };
}

function ReportKpi({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
}) {
  return (
    <div className="ocf-card ocf-kpi oht-report-kpi">
      <div className="ocf-kpi-label">{label}</div>
      <div className="ocf-big-number">{value}</div>
      <div className="ocf-muted">{hint}</div>
    </div>
  );
}

function ReportsTable({
  rows,
  loading,
}: {
  rows: ReportDisplayRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="oht-report-table-empty">
        Loading report data…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="oht-report-table-empty">
        No report rows match the selected filters.
      </div>
    );
  }

  return (
    <div className="oht-report-table-wrap">
      <table className="oht-report-table">
        <thead>
          <tr>
            <th>FSA</th>
            <th>City</th>
            <th>Clinics</th>
            <th>Doctors</th>
            <th>Attached #</th>
            <th>Pipeline #</th>
            <th>Pace</th>
            <th>Avg TTA</th>
            <th>Remaining Capacity</th>
            <th>Target</th>
            <th>ETA</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <strong>{row.fsa || "—"}</strong>
              </td>

              <td>{row.city || "—"}</td>

              <td>{fmtNumber(row.clinics)}</td>

              <td>{fmtNumber(row.doctors)}</td>

              <td>{fmtNumber(row.attachedCount)}</td>

              <td>{fmtNumber(row.pipelineCount)}</td>

              <td>{fmtPace(row.pacePerDay)}</td>

              <td>{fmtDays(row.avgDaysToAttachment)}</td>

              <td>{fmtNumber(row.remainingCapacity)}</td>

              <td>{fmtNumber(row.targetPatients)}</td>

              <td>
                <span className={targetClass(row)}>
                  {fmtTargetEta(row.estimatedDaysToTarget)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InsightsPage() {
  const [timeline, setTimeline] = useState<TimelineState>(
    resolveTimeline("last90")
  );

  const [cityFilter, setCityFilter] = useState("");
  const [fsaFilter, setFsaFilter] = useState("");
  const [baselineStatus, setBaselineStatus] =
    useState<OhtPatientBaselineStatus>("all");

  const [insights, setInsights] = useState<OhtInsightsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rawReportRows = useMemo(
    () => insights?.reportRows || [],
    [insights?.reportRows]
  );

  const reportRows = useMemo(
    () => buildDisplayRows(rawReportRows, getWindowDays(insights)),
    [insights, rawReportRows]
  );

  const cityOptions = useMemo(() => {
    const fromRows = uniqueSorted(rawReportRows.map((row) => row.city));
    const fromCities = uniqueSorted(
      (insights?.cities || []).map((city) => city.cityName)
    );

    return uniqueSorted([...fromRows, ...fromCities]);
  }, [insights?.cities, rawReportRows]);

  const fsaOptions = useMemo(() => {
    const fromRows = uniqueSorted(rawReportRows.map((row) => row.fsa));
    const fromFsas = uniqueSorted((insights?.fsas || []).map((fsa) => fsa.fsa));

    return uniqueSorted([...fromRows, ...fromFsas]);
  }, [insights?.fsas, rawReportRows]);

  const summary = useMemo(() => buildSummary(rawReportRows), [rawReportRows]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (timeline.key !== "last90") count += 1;
    if (cityFilter) count += 1;
    if (fsaFilter) count += 1;
    if (baselineStatus !== "all") count += 1;
    return count;
  }, [baselineStatus, cityFilter, fsaFilter, timeline.key]);

  const exportReport = useMemo(
    () =>
      buildReportExport({
        rows: reportRows,
        insights,
        timeline,
        cityFilter,
        fsaFilter,
        baselineStatus,
        summary,
      }),
    [baselineStatus, cityFilter, fsaFilter, insights, reportRows, summary, timeline]
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const response = await getOhtInsights(
          {
            from: timeline.from,
            to: timeline.to,
            city: cityFilter || null,
            fsa: fsaFilter || null,
            baselineStatus,
          },
          controller.signal
        );

        if (cancelled) return;

        setInsights(response);
      } catch (err: any) {
        if (cancelled || err?.name === "AbortError") return;
        setError(String(err?.message || "Failed to load OHT reports."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [baselineStatus, cityFilter, fsaFilter, timeline]);

  function handleTimelineChange(nextKey: TimelineKey) {
    if (nextKey === "custom") {
      setTimeline((current) => ({
        key: "custom",
        from: current.from || isoDaysAgo(90),
        to: current.to || TODAY_ISO,
      }));
      return;
    }

    setTimeline(resolveTimeline(nextKey));
  }

  function handleCustomFromChange(value: string) {
    setTimeline((current) => ({
      ...current,
      key: "custom",
      from: value || null,
    }));
  }

  function handleCustomToChange(value: string) {
    setTimeline((current) => ({
      ...current,
      key: "custom",
      to: value || null,
    }));
  }

  function resetFilters() {
    setTimeline(resolveTimeline("last90"));
    setCityFilter("");
    setFsaFilter("");
    setBaselineStatus("all");
  }

  async function onExportCsv() {
    setExportBusy(true);

    try {
      downloadStructuredReportCsv(exportReport, `oht-reports-${timeline.key}`);
    } finally {
      setExportBusy(false);
    }
  }

  async function onExportPdf() {
    setExportBusy(true);

    try {
      downloadReportPdf(exportReport, `oht-reports-${timeline.key}`);
    } finally {
      setExportBusy(false);
    }
  }

  return (
    <div className="oht-page">
      <header className="oht-page-header">
        <div className="oht-page-title-row">
          <div>
            <h1 className="oht-page-title">Reports</h1>
            <p className="oht-page-subtitle">
              Attachment, capacity, pipeline, and target performance across the
              OHT. Reports are scoped by the OHT’s FSA coverage and generated
              from live platform data.
            </p>
          </div>

          <div className="oht-report-header-actions">
            <button
              type="button"
              className="oht-report-secondary-button"
              onClick={resetFilters}
            >
              Reset filters
            </button>

            <button
              type="button"
              className="oht-report-secondary-button"
              onClick={onExportPdf}
              disabled={reportRows.length === 0 || loading || exportBusy}
            >
              {exportBusy ? "Building…" : "Export PDF"}
            </button>

            <button
              type="button"
              className="oht-report-primary-button"
              onClick={onExportCsv}
              disabled={reportRows.length === 0 || loading || exportBusy}
            >
              {exportBusy ? "Building…" : "Export CSV"}
            </button>
          </div>
        </div>
      </header>

      <section className="ocf-card oht-filters oht-report-filters">
        <div className="oht-filters-head">
          <div>
            <h2 className="ocf-section-title">Filters</h2>
            <p className="oht-page-subtitle" style={{ marginTop: 4 }}>
              Timeline, city, FSA, and patient baseline status.
            </p>
          </div>

          <span className="oht-section-hint">
            {timelineLabel(timeline)} · {baselineLabel(baselineStatus)}
            {activeFilterCount > 0 ? ` · ${activeFilterCount} active filter(s)` : ""}
          </span>
        </div>

        <div className="oht-report-filter-grid">
          <label className="oht-report-filter-field">
            <span>Timeline</span>
            <select
              value={timeline.key}
              onChange={(event) =>
                handleTimelineChange(event.target.value as TimelineKey)
              }
            >
              <option value="last30">Last 30 days</option>
              <option value="last90">Last 90 days</option>
              <option value="ytd">Year to date</option>
              <option value="all">All time</option>
              <option value="custom">Custom</option>
            </select>
          </label>

          {timeline.key === "custom" ? (
            <>
              <label className="oht-report-filter-field">
                <span>From</span>
                <input
                  type="date"
                  value={timeline.from || ""}
                  onChange={(event) =>
                    handleCustomFromChange(event.target.value)
                  }
                />
              </label>

              <label className="oht-report-filter-field">
                <span>To</span>
                <input
                  type="date"
                  value={timeline.to || ""}
                  onChange={(event) => handleCustomToChange(event.target.value)}
                />
              </label>
            </>
          ) : null}

          <label className="oht-report-filter-field">
            <span>City</span>
            <select
              value={cityFilter}
              onChange={(event) => setCityFilter(event.target.value)}
            >
              <option value="">All cities</option>
              {cityOptions.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </label>

          <label className="oht-report-filter-field">
            <span>FSA</span>
            <select
              value={fsaFilter}
              onChange={(event) => setFsaFilter(event.target.value)}
            >
              <option value="">All FSAs</option>
              {fsaOptions.map((fsa) => (
                <option key={fsa} value={fsa}>
                  {fsa}
                </option>
              ))}
            </select>
          </label>

          <label className="oht-report-filter-field">
            <span>Patient baseline status</span>
            <select
              value={baselineStatus}
              onChange={(event) =>
                setBaselineStatus(
                  event.target.value as OhtPatientBaselineStatus
                )
              }
            >
              <option value="all">All patients</option>
              <option value="unattached">MOH-unattached patients</option>
              <option value="attached">
                Patients switching / already attached
              </option>
            </select>
          </label>
        </div>
      </section>

      {error ? <div className="oht-page-error">{error}</div> : null}

      <section className="oht-report-kpi-grid">
        <ReportKpi
          label="Requests"
          value={loading ? "…" : fmtNumber(summary.requests)}
          hint="Submitted requests in selected timeline."
        />

        <ReportKpi
          label="Attached"
          value={loading ? "…" : fmtNumber(summary.attached)}
          hint="Completed attachments in selected timeline."
        />

        <ReportKpi
          label="Pipeline"
          value={loading ? "…" : fmtNumber(summary.pipeline)}
          hint="Current active patient flow."
        />

        <ReportKpi
          label="Remaining Capacity"
          value={loading ? "…" : fmtNumber(summary.remainingCapacity)}
          hint="Current reported remaining capacity."
        />

        <ReportKpi
          label="Target"
          value={loading ? "…" : fmtNumber(summary.target)}
          hint="Active OHT/FSA target coverage."
        />
      </section>

      <section className="ocf-card oht-section oht-report-section">
        <div className="oht-section-head">
          <div>
            <h2 className="ocf-section-title">Report table</h2>
            <p className="oht-page-subtitle" style={{ marginTop: 6 }}>
              FSA and city-level reporting. Clinics are shown as counts to keep
              the report readable on screen.
            </p>
          </div>

          <span className="oht-section-hint">
            {loading
              ? "Loading rows…"
              : `${fmtNumber(reportRows.length)} row(s)`}
          </span>
        </div>

        <ReportsTable rows={reportRows} loading={loading} />

        <div className="oht-report-footnote">
          <strong>Definitions:</strong> Pipeline includes submitted, in-review,
          waitlisted, and accepted-pending-attachment requests. Pace is completed
          attachments per day within the selected timeline. Avg TTA is the
          average number of days from request submission to attachment completion.
        </div>
      </section>

      <PageOutline
        whatItShows="This page shows an exportable attachment report for the OHT, including patient requests, completed attachments, active pipeline, remaining capacity, FSA targets, attachment pace, average time to attachment, and estimated time to target."
        audience="For OHT Admin users who need a clear reporting view for operational planning, internal updates, OHT discussions, and progress monitoring."
        updateTrigger="The report refreshes when you change the timeline, city, FSA, or patient baseline status. The PDF and CSV exports use the same filtered report shown on the page."
        dataSources={[
          {
            label: "Attachment activity",
            status: "live",
            detail: "Shows submitted requests and completed attachments for the selected reporting period.",
          },
          {
            label: "Capacity and pipeline",
            status: "live",
            detail: "Shows current remaining capacity and active patient flow across the selected OHT area.",
          },
          {
            label: "FSA and city reporting",
            status: "live",
            detail: "Groups report results by FSA and city so the OHT can understand attachment progress by community.",
          },
          {
            label: "Targets and projections",
            status: "live",
            detail: "Shows active target coverage, current progress toward target, attachment pace, and estimated time to target when there is enough pace data.",
          },
          {
            label: "Exports",
            status: "derived",
            detail: "PDF and CSV exports are generated from the same filtered report currently shown on screen.",
          },
        ]}
        notes={[
          "Use this page when you need a clean OHT-level report rather than a detailed clinic or doctor list.",
          "The report is designed for planning and monitoring, not for ranking clinics or doctors.",
          "Remaining capacity is a current planning signal and should not be treated as a guaranteed number of available attachments.",
          "Estimated time to target depends on the current attachment pace and may change as new attachments are completed.",
          "Filters only change the report view and exported file; they do not change patient requests, clinic records, doctor profiles, or targets.",
        ]}
      />
    </div>
  );
}